import { useState } from 'react';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import { parseDate } from '@guildeducationinc/recess';
import { useAuth } from '@guildeducationinc/guild-auth';
import { datadogRum } from '@datadog/browser-rum';
import {
  PROGRAM_COMPLETIONS_BY_UUID,
  UPDATE_PROGRAM_COMPLETION,
  CREATE_PROGRAM_COMPLETION,
  DELETE_PROGRAM_COMPLETION,
} from '@src/graphql/profile_edit/queries/programCompletionDates';
import {
  PersistedProgramCompletion,
  ProgramCompletionQueryResponse,
  ProgramCompletionQueryVariables,
  ProgramCompletionMutationResponse,
  ProgramCompletionUpdateVariables,
  ProgramCompletionDeleteResponse,
  ProgramCompletionDeleteVariables,
  DirtyProgramCompletion,
} from '@src/components/Pages/EditProfile/ProgramCompletion/ProgramCompletion';
import { Server } from '@src/components/GqlProvider/GqlProvider';
import { DEFAULT_OPTION as ACADEMIC_PARTNER_DEFAULT_OPTION } from '@src/components/Pages/EditProfile/Fields/AcademicPartnerSelectField/AcademicPartnerSelectField';
import { DEFAULT_OPTION as PROGRAM_DEFAULT_OPTION } from '@src/components/Pages/EditProfile/Fields/ProgramSelectField/ProgramSelectField';

export const formatCompletionForUser = (
  completion: PersistedProgramCompletion
): DirtyProgramCompletion => {
  replaceFalsyUuids(completion);
  return {
    ...completion,
    isCompleted: !!completion.actualGraduationDate,
    isEditing: false,
  };
};

export const replaceFalsyUuids = (completion: PersistedProgramCompletion) => {
  if (!completion.academicPartnerUuid) {
    completion.academicPartnerUuid = ACADEMIC_PARTNER_DEFAULT_OPTION.value;
  }
  if (!completion.programUuid) {
    completion.programUuid = PROGRAM_DEFAULT_OPTION.value;
  }
  if (!completion.programType) {
    completion.programType = 'not_found';
  }
};

export const sortCompletionsByDate = (
  completions: DirtyProgramCompletion[],
  dateType: string
): DirtyProgramCompletion[] => {
  const dateAttribute = dateType === 'expected' ? 'expectedGraduationDate' : 'actualGraduationDate';
  return completions.sort((completion1, completion2) => {
    const date1 = parseDate(completion1[dateAttribute], 'MM/dd/yyyy') as Date;
    const date2 = parseDate(completion2[dateAttribute], 'MM/dd/yyyy') as Date;

    return date2.getTime() - date1.getTime();
  });
};

const useProgramCompletion = () => {
  const { user } = useAuth();
  const [programCompletions, setProgramCompletions] = useState([] as DirtyProgramCompletion[]);

  const sortedCompletedPrograms = sortCompletionsByDate(
    programCompletions.filter((completion) => !!completion.actualGraduationDate),
    'actual'
  );

  const sortedInProgressPrograms = sortCompletionsByDate(
    programCompletions.filter((completion) => !completion.actualGraduationDate),
    'expected'
  );

  const onError = (error: ApolloError): void => {
    const { graphQLErrors = [], networkError } = error;

    const errorTitle = 'There was an error saving program completion data';

    if (graphQLErrors.length) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        datadogRum.addError(error, {
          message,
          errorTitle,
          locations,
          path,
        });
      });
    }

    if (networkError) {
      datadogRum.addError(networkError, {
        message: errorTitle,
      });
    }
  };

  const [queryProgramCompletions, { loading: loadingProgramCompletions }] = useLazyQuery<
    ProgramCompletionQueryResponse,
    ProgramCompletionQueryVariables
  >(PROGRAM_COMPLETIONS_BY_UUID, {
    context: {
      server: Server.SELF_REPORTED_DATA,
    },
    variables: {
      studentUuid: user?.id,
    },
    // TODO: this is throwing a state update error in tests
    onCompleted: (data) => {
      const { programCompletions } = data || {};

      if (programCompletions) {
        setProgramCompletions(
          programCompletions.map((unformattedCompletion) => {
            return formatCompletionForUser(unformattedCompletion);
          })
        );
      }
    },
    fetchPolicy: 'no-cache',
    onError,
  });

  const [createProgramCompletion, { error: createProgramCompletionError }] = useMutation<
    {
      createCompletion: ProgramCompletionMutationResponse;
    },
    {
      completionData: DirtyProgramCompletion;
    }
  >(CREATE_PROGRAM_COMPLETION, {
    context: {
      server: Server.SELF_REPORTED_DATA,
    },
    onError,
  });

  const [updateProgramCompletion, { error: updateProgramCompletionError }] = useMutation<
    {
      updateCompletion: ProgramCompletionMutationResponse;
    },
    ProgramCompletionUpdateVariables
  >(UPDATE_PROGRAM_COMPLETION, {
    context: {
      server: Server.SELF_REPORTED_DATA,
    },
    onError,
  });

  const [deleteProgramCompletion, { error: deleteProgramCompletionError }] = useMutation<
    ProgramCompletionDeleteResponse,
    ProgramCompletionDeleteVariables
  >(DELETE_PROGRAM_COMPLETION, {
    context: {
      server: Server.SELF_REPORTED_DATA,
    },
    onError,
  });

  return {
    queryProgramCompletions,
    deleteProgramCompletion,
    deleteProgramCompletionError,
    loadingProgramCompletions,
    createProgramCompletion,
    createProgramCompletionError,
    updateProgramCompletion,
    updateProgramCompletionError,
    sortedCompletedPrograms,
    sortedInProgressPrograms,
  };
};

export default useProgramCompletion;
